import { createContext, useContext, useMemo, useState } from "react";

import type { ReactNode } from "react";

interface NotificationUpdate {
  notifications: Notification[];
  totalUnreadCount: number;
  totalReadCount: number;
}

interface NotificationContextState {
  notifications: Notification[];
  totalUnreadCount: number;
  totalReadCount: number;
  updateNotifications: (update: NotificationUpdate) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const NotificationContext = createContext<NotificationContextState | undefined>(
  undefined,
);

interface NotificationProviderProps {
  children: ReactNode;
}

const EMPTY_NOTIFICATIONS: NotificationUpdate = {
  notifications: [],
  totalUnreadCount: 0,
  totalReadCount: 0,
};

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notificationState, setNotificationState] =
    useState<NotificationUpdate>(EMPTY_NOTIFICATIONS);
  const [loading, setLoading] = useState<boolean>(true);

  const updateNotifications = setNotificationState;

  const value = useMemo(
    () => ({
      ...notificationState,
      updateNotifications,
      loading,
      setLoading,
    }),
    [notificationState, updateNotifications, loading, setLoading],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext(): NotificationContextState {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider",
    );
  }
  return context;
}
